<!-- Responsibility: Implement action input behavior for the ui/components surface.
Scope: Owns ui/components concerns only; cross-surface orchestration is excluded. -->
<action-input>
  <form
    aria-busy={ state.isProcessing ? 'true' : 'false' }
    data-processing={ state.isProcessing ? 'true' : 'false' }
    data-submitting={ state.isSubmitting ? 'true' : 'false' }
    onsubmit={ onSubmit }
  >
    <ul if={ (state.suggestions ?? []).length }>
      <li
        each={ s in (state.suggestions ?? []) }
      >
        <button class="chip" type="button" onclick={ () => inspectSuggestion(s) }>{ s.short }</button>
      </li>
    </ul>
    <input
      ref="input"
      type="text"
      placeholder={ state.isProcessing ? state.processingText : 'TRANSMIT INTENT...' }
      disabled={ state.isProcessing }
      autocomplete="off"
    >
  </form>

  <script>
    import { dispatcher } from '../../app-states.js'
    import { buildInfoDialog } from '../dialog/dialog-openers.ts'
    import { TOPICS } from "@anyquest/shared-core"
    import * as gameStore from '../../game-store.js'
    import { PROCESS_LINES, DEFAULT_PROCESS_LINE } from '../helpers/action-processing.ts'
    import { buildToolRef } from '../helpers/action-refs.ts'

    export default {
      state: { suggestions: [], isProcessing: false, isSubmitting: false, processingText: DEFAULT_PROCESS_LINE, activeActionId: null },

      onMounted() {
        this.handles = [
          dispatcher.on(TOPICS.game.actionStart,    (_e, d)    => { this.startProcessing(d?.actionId) }),
          dispatcher.on(TOPICS.game.actionComplete, (_e, d) => {
            if (!this.shouldHandleTerminalEvent(d)) return
            this.stopProcessing()
            if (!d?.isCompanionAction && !d?.awaitArgs) {
              const last = d?.state?.history?.slice(-1)[0]
              this.update({ suggestions: last?.suggestions ?? [] })
            }
          }),
          dispatcher.on(TOPICS.game.actionFailed,   (_e, d)    => {
            if (!this.shouldHandleTerminalEvent(d)) return
            this.stopProcessing()
          }),
          dispatcher.on(TOPICS.game.loaded,  (_e, gs) => { this.loadSuggestions(gs) }),
          dispatcher.on(TOPICS.game.started, (_e, gs) => { this.loadSuggestions(gs) }),
        ]
        this.onResize = () => this.syncViewportOffset()
        window.addEventListener('resize', this.onResize, { passive: true })
        if (window.visualViewport) {
          window.visualViewport.addEventListener('resize', this.onResize, { passive: true })
        }
        this.syncViewportOffset()
        // Sync immediately if game already loaded when this component mounts
        if (gameStore.state) this.loadSuggestions(gameStore.state)
      },

      onUnmounted() {
        this.handles?.forEach(h => dispatcher.off(h))
        clearInterval(this.processingTicker)
        clearTimeout(this.processingWatchdog)
        clearTimeout(this.submitPulseTimer)
        clearTimeout(this.queuedActionTimer)
        window.removeEventListener('resize', this.onResize)
        if (window.visualViewport) {
          window.visualViewport.removeEventListener('resize', this.onResize)
        }
        document.documentElement.style.removeProperty('--am2-vk-offset')
      },

      syncViewportOffset() {
        const vv = window.visualViewport
        if (!vv) {
          if (this.lastVkOffset !== '0') {
            document.documentElement.style.setProperty('--am2-vk-offset', '0')
            this.lastVkOffset = '0'
          }
          return
        }
        const overlap = Math.max(0, Math.round(window.innerHeight - (vv.height + vv.offsetTop)))
        const next = `${overlap}px`
        if (this.lastVkOffset !== next) {
          document.documentElement.style.setProperty('--am2-vk-offset', next)
          this.lastVkOffset = next
        }
      },

      loadSuggestions(gs) {
        const last = gs?.history?.slice(-1)[0]
        this.update({ suggestions: last?.suggestions ?? [], isProcessing: false, isSubmitting: false, activeActionId: null })
      },

      onSubmit(event) {
        event?.preventDefault?.()
        const input = this.$('input[ref="input"]')
        const val = String(input?.value ?? '').trim()
        if (!val) return
        this.triggerSubmitPulse()
        if (input) input.value = ''
        clearTimeout(this.queuedActionTimer)
        this.queuedActionTimer = setTimeout(() => this.doAction(val), 40)
      },

      triggerSubmitPulse() {
        if (this.submitPulseTimer) clearTimeout(this.submitPulseTimer)
        this.update({ isSubmitting: true })
        this.submitPulseTimer = setTimeout(() => this.update({ isSubmitting: false }), 320)
      },

      startProcessing(actionId) {
        clearInterval(this.processingTicker)
        clearTimeout(this.processingWatchdog)

        this.processingIdx = 0
        this.update({
          isProcessing: true,
          processingText: DEFAULT_PROCESS_LINE,
          activeActionId: Number.isFinite(actionId) ? actionId : null,
        })

        this.processingTicker = setInterval(() => {
          this.processingIdx = (this.processingIdx + 1) % PROCESS_LINES.length
          this.update({ processingText: PROCESS_LINES[this.processingIdx] })
        }, 1300)

        // Failsafe: never leave the input hard-locked indefinitely.
        this.processingWatchdog = setTimeout(() => {
          this.stopProcessing()
        }, 45000)
      },

      stopProcessing() {
        clearInterval(this.processingTicker)
        clearTimeout(this.processingWatchdog)
        this.update({ isProcessing: false, isSubmitting: false, processingText: DEFAULT_PROCESS_LINE, activeActionId: null })
        this.$('input[ref="input"]')?.focus()
      },

      shouldHandleTerminalEvent(data) {
        const current = this.state.activeActionId
        if (current == null) return true
        const incoming = data?.actionId
        if (!Number.isFinite(incoming)) return true
        return Number(incoming) === Number(current)
      },

      doAction(input) {
        if (/^talk to\s+.+/i.test(input) && !/:/.test(input)) {
          const targetName = input.replace(/^talk to\s+/i, '').trim()
          dispatcher.trigger('action-input', TOPICS.ui.dialogOpen, {
            content: 'talk-dialog',
            data: {
              targetName,
              baseInput: `Talk to ${targetName}`,
            },
            options: {
              title: targetName ? `TALK TO ${targetName.toUpperCase()}` : 'TALK',
            },
          })
          return
        }
        gameStore.handleAction(input)
      },

      inspectSuggestion(s) {
        dispatcher.trigger('action-input', TOPICS.ui.dialogOpen, buildInfoDialog({
          options: {
            title: s.short,
            desc: s.full,
            actions: [{
              label: 'EXECUTE',
              input: s.full,
              source: 'player',
              mode: 'do',
              category: 'other',
              tool: buildToolRef('free-text', s.short || 'suggestion', s.short || 'Suggestion'),
              tags: ['suggestion_execute', 'action_input'],
            }],
          },
        }))
      },
    }
  </script>
</action-input>
